/**
 * WebSocket Service
 * Provides real-time updates for job status changes
 *
 * Authentication:
 * - Fetches a short-lived token from /v1/user/ws-token before connecting
 * - Token is passed as query parameter: /ws?token=xxx
 * - Falls back to polling if token fetch fails (user not authenticated)
 */

import { getDefaultBaseUrl } from './config'

// ============================================================================
// Types
// ============================================================================

/** Response from GET /v1/user/ws-token */
interface WSTokenResponse {
  token: string
  expiresIn: number // seconds until expiry
}

export type MessageType =
  | 'subscribe'
  | 'unsubscribe'
  | 'job_update'
  | 'jobs_count'
  | 'ping'
  | 'pong'

export interface WebSocketMessage {
  type: MessageType
  projectId?: string
  jobId?: string
  status?: string
  count?: number
  data?: unknown
}

export interface WebSocketClientConfig {
  /** Gateway base URL (defaults to environment config) */
  baseUrl?: string
  /** Reconnect on disconnect (default: true) */
  autoReconnect?: boolean
  /** Max reconnect attempts (default: 10) */
  maxReconnectAttempts?: number
  /** Initial reconnect delay in ms (default: 1000) */
  reconnectDelay?: number
  /** Max reconnect delay in ms (default: 30000) */
  maxReconnectDelay?: number
}

type EventCallback = (message: WebSocketMessage) => void

export interface WebSocketClient {
  /** Connect to WebSocket server (fetches auth token first) */
  connect(): Promise<void>
  /** Disconnect from server */
  disconnect(): void
  /** Subscribe to a project's updates */
  subscribeToProject(projectId: string): void
  /** Unsubscribe from project updates */
  unsubscribeFromProject(): void
  /** Add event listener */
  on(
    event: 'message' | 'connect' | 'disconnect' | 'error',
    callback: EventCallback
  ): () => void
  /** Check if connected */
  isConnected(): boolean
}

// ============================================================================
// Implementation
// ============================================================================

const DEFAULT_CONFIG: Required<WebSocketClientConfig> = {
  baseUrl: '',
  autoReconnect: true,
  maxReconnectAttempts: 10,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
}

// ============================================================================
// Token Fetching
// ============================================================================

/**
 * Fetch a short-lived WebSocket authentication token.
 * Returns null if fetch fails (e.g., user not authenticated).
 */
async function fetchWSToken(baseUrl: string): Promise<string | null> {
  try {
    const origin = baseUrl || window.location.origin
    const url = `${origin}/v1/user/ws-token`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // OAuth cookie auth
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      // User not authenticated or other error - fail silently
      return null
    }

    const data: WSTokenResponse = await response.json()
    return data.token
  } catch {
    // Network error or other failure - fail silently
    return null
  }
}

/**
 * Create a WebSocket client for real-time updates
 */
export function createWebSocketClient(
  config: WebSocketClientConfig = {}
): WebSocketClient {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config }
  const baseUrl = mergedConfig.baseUrl || getDefaultBaseUrl()

  let ws: WebSocket | null = null
  let reconnectAttempts = 0
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let currentProjectId: string | null = null
  let isManualDisconnect = false
  let pingInterval: ReturnType<typeof setInterval> | null = null
  let isConnecting = false

  // Error logging state - only log the first error to reduce console noise
  let hasLoggedError = false

  const listeners: {
    message: EventCallback[]
    connect: EventCallback[]
    disconnect: EventCallback[]
    error: EventCallback[]
  } = {
    message: [],
    connect: [],
    disconnect: [],
    error: [],
  }

  function emit(
    event: 'message' | 'connect' | 'disconnect' | 'error',
    message: WebSocketMessage
  ): void {
    listeners[event].forEach((callback) => callback(message))
  }

  function getWebSocketUrl(token?: string): string {
    // Convert HTTP URL to WebSocket URL
    const httpUrl = baseUrl || window.location.origin
    const wsProtocol = httpUrl.startsWith('https') ? 'wss' : 'ws'
    const host = httpUrl.replace(/^https?:\/\//, '')
    const wsUrl = `${wsProtocol}://${host}/ws`

    // Append token as query parameter if provided
    if (token) {
      return `${wsUrl}?token=${encodeURIComponent(token)}`
    }
    return wsUrl
  }

  function scheduleReconnect(): void {
    if (!mergedConfig.autoReconnect || isManualDisconnect) return
    if (reconnectAttempts >= mergedConfig.maxReconnectAttempts) {
      // Only log once when max attempts reached
      if (!hasLoggedError) {
        console.warn('[WebSocket] Max reconnect attempts reached')
        hasLoggedError = true
      }
      return
    }

    // Exponential backoff
    const delay = Math.min(
      mergedConfig.reconnectDelay * Math.pow(2, reconnectAttempts),
      mergedConfig.maxReconnectDelay
    )
    reconnectAttempts++

    reconnectTimeout = setTimeout(() => {
      void connect()
    }, delay)
  }

  function startPingInterval(): void {
    // Send ping every 30 seconds to keep connection alive
    pingInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000)
  }

  function stopPingInterval(): void {
    if (pingInterval) {
      clearInterval(pingInterval)
      pingInterval = null
    }
  }

  async function connect(): Promise<void> {
    if (
      ws?.readyState === WebSocket.OPEN ||
      ws?.readyState === WebSocket.CONNECTING ||
      isConnecting
    ) {
      return
    }

    isManualDisconnect = false
    isConnecting = true

    try {
      // Fetch authentication token first
      const token = await fetchWSToken(baseUrl)

      // If token fetch failed (user not authenticated), skip WebSocket connection
      // This is silent - the app will fall back to polling
      if (!token) {
        isConnecting = false
        return
      }

      const url = getWebSocketUrl(token)
      ws = new WebSocket(url)

      ws.onopen = () => {
        reconnectAttempts = 0
        hasLoggedError = false // Reset error logging state on successful connect
        emit('connect', { type: 'ping' })
        startPingInterval()

        // Re-subscribe to project if we were previously subscribed
        if (currentProjectId) {
          subscribeToProject(currentProjectId)
        }
      }

      ws.onclose = () => {
        stopPingInterval()
        emit('disconnect', { type: 'ping' })
        scheduleReconnect()
      }

      ws.onerror = () => {
        // Only log the first error to reduce console noise
        if (!hasLoggedError) {
          hasLoggedError = true
        }
        emit('error', { type: 'ping' })
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage
          emit('message', message)
        } catch {
          // Silent fail on parse errors - don't spam console
        }
      }
    } catch {
      // Only log first connection error
      if (!hasLoggedError) {
        hasLoggedError = true
      }
      scheduleReconnect()
    } finally {
      isConnecting = false
    }
  }

  function disconnect(): void {
    isManualDisconnect = true
    stopPingInterval()

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }

    if (ws) {
      ws.close(1000, 'Client disconnect')
      ws = null
    }

    // Reset error logging state on manual disconnect
    hasLoggedError = false
  }

  function subscribeToProject(projectId: string): void {
    currentProjectId = projectId
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'subscribe', projectId }))
    }
  }

  function unsubscribeFromProject(): void {
    currentProjectId = null
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'unsubscribe' }))
    }
  }

  function on(
    event: 'message' | 'connect' | 'disconnect' | 'error',
    callback: EventCallback
  ): () => void {
    listeners[event].push(callback)
    return () => {
      const index = listeners[event].indexOf(callback)
      if (index > -1) {
        listeners[event].splice(index, 1)
      }
    }
  }

  function isConnected(): boolean {
    return ws?.readyState === WebSocket.OPEN
  }

  return {
    connect,
    disconnect,
    subscribeToProject,
    unsubscribeFromProject,
    on,
    isConnected,
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let wsClient: WebSocketClient | null = null

/**
 * Get or create the singleton WebSocket client
 */
export function getWebSocketClient(): WebSocketClient {
  if (!wsClient) {
    wsClient = createWebSocketClient()
  }
  return wsClient
}

/**
 * Reset the singleton client (for testing)
 */
export function resetWebSocketClient(): void {
  if (wsClient) {
    wsClient.disconnect()
    wsClient = null
  }
}
